import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Mail,
  Search,
  Globe,
  Code2,
  BarChart3,
  MapPin,
  Sparkles,
  CheckCircle2,
  Rocket,
  LineChart,
  Layers,
} from "lucide-react";
import { seoHead, SITE_ORIGIN } from "@/lib/seo";
import { faqSchema, FAQAccordion } from "@/components/FAQAccordion";
import { AnimatedSection } from "@/components/AnimatedSection";
import { trackEvent } from "@/lib/tracking";

const FOUNDER_EMAIL = "syedbiz96@gmail.com";
const RML_URL = "https://rankmylocal.co.uk";

/** Build a mailto: link with a helpful, pre-filled subject and body. */
function mailtoHref(subject: string) {
  const body = [
    "Hi,",
    "",
    "I found your work on woodseerautos.co.uk and I'd like to talk about my own website / local SEO.",
    "",
    "A bit about my business:",
    "- Business name:",
    "- Location:",
    "- What I need help with:",
    "",
    "Thanks,",
  ].join("\n");
  return `mailto:${FOUNDER_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
}

function handleContactClick(cta: string) {
  trackEvent("web_design_contact_click", { cta, destination: "email" });
}

function handleRmlClick(cta: string) {
  trackEvent("web_design_outbound_click", { cta, destination: "rankmylocal" });
}

const faqs = [
  {
    q: "Who designed and built this website?",
    a: "This site was designed, built and is managed by RankMyLocal — a UK local SEO and web design practice. Every page, animation, area landing page and piece of schema on woodseerautos.co.uk was hand-built as part of an ongoing engagement.",
  },
  {
    q: "Do you take on new clients?",
    a: "Yes. RankMyLocal works with a small number of independent UK businesses at any one time — mostly garages, trades, restaurants and professional services. Enquiries go directly to the founder and are usually answered within one working day.",
  },
  {
    q: "What does a project look like?",
    a: "Most engagements start with a free 30-minute audit call. From there it's typically a website rebuild, Google Business Profile optimisation and 3–6 months of local SEO work to establish rankings. Ongoing management is optional.",
  },
  {
    q: "How much does it cost?",
    a: "Website builds start in the low four figures and monthly management is priced per business. There is no fixed rate card — pricing is scoped after the audit call so you only pay for what your business actually needs.",
  },
  {
    q: "What's the fastest way to get in touch?",
    a: `Email the founder directly at ${FOUNDER_EMAIL}. Include your business name, location and what you'd like to improve — you'll get a personal reply, not an automated sequence.`,
  },
];

const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Syed",
  jobTitle: "Founder & Lead Designer, RankMyLocal",
  email: `mailto:${FOUNDER_EMAIL}`,
  url: `${SITE_ORIGIN}/web-design`,
  worksFor: {
    "@type": "Organization",
    name: "RankMyLocal",
    url: RML_URL,
  },
  knowsAbout: [
    "Local SEO",
    "Web design",
    "UX/UI design",
    "Google Business Profile optimisation",
    "Schema markup",
    "Conversion-focused websites",
  ],
};

export const Route = createFileRoute("/web-design")({
  head: () =>
    seoHead({
      path: "/web-design",
      title: "Website Design & Local SEO by RankMyLocal | Woodseer Autos",
      description:
        "This site was designed by RankMyLocal — a UK web design and local SEO practice. Meet the founder behind the build and enquire about your own project.",
      ogType: "profile",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Web Design & SEO", path: "/web-design" },
      ],
      jsonLd: [personSchema, faqSchema(faqs)],
    }),
  component: WebDesignPage,
});

const CAPABILITIES = [
  {
    icon: Globe,
    title: "Custom website design",
    body: "Hand-designed, mobile-first sites built for conversion — not templates. Every layout, animation and micro-interaction is intentional.",
  },
  {
    icon: Search,
    title: "Local SEO that ranks",
    body: "Complete on-page SEO, schema, area pages and Google Business Profile work — the same stack that got Woodseer Autos into the E14 map pack.",
  },
  {
    icon: Code2,
    title: "Modern, fast tech stack",
    body: "React, TanStack, edge-rendered pages, image optimisation and Core Web Vitals baked in. No page-builder bloat.",
  },
  {
    icon: BarChart3,
    title: "Analytics & attribution",
    body: "GA4, Search Console, call tracking and UTM-tagged CTAs so every enquiry is traceable back to a channel.",
  },
  {
    icon: MapPin,
    title: "Google Business Profile",
    body: "Category tuning, service population, NAP consistency across directories and a review response strategy that actually gets used.",
  },
  {
    icon: Layers,
    title: "Ongoing management",
    body: "Optional monthly retainer covering content updates, technical monitoring, ranking reports and continuous conversion improvements.",
  },
];

const PROCESS = [
  {
    icon: Mail,
    title: "1. Email the founder",
    body: `Send a short note to ${FOUNDER_EMAIL} describing your business. You'll get a personal reply within one working day.`,
  },
  {
    icon: Sparkles,
    title: "2. Free 30-min audit call",
    body: "We look at your current site, Google Business Profile and competitors, and agree whether there's a fit.",
  },
  {
    icon: Rocket,
    title: "3. Build & launch",
    body: "Design in the browser, weekly previews, honest timelines. Most sites go live in 3–6 weeks.",
  },
  {
    icon: LineChart,
    title: "4. Rank & iterate",
    body: "Ongoing local SEO, content and conversion work. Monthly report, no lock-in contracts.",
  },
];

function WebDesignPage() {
  return (
    <main>
      {/* Breadcrumb */}
      <nav
        aria-label="Breadcrumb"
        className="bg-secondary/50 border-b border-border/30"
      >
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-2.5">
          <ol className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            <li>
              <Link
                to="/"
                className="hover:text-foreground hover:underline underline-offset-4"
              >
                Home
              </Link>
            </li>
            <li aria-hidden="true">›</li>
            <li className="font-medium text-foreground" aria-current="page">
              Web Design & SEO
            </li>
          </ol>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative surface-dark grain-overlay overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1 bg-primary" aria-hidden="true" />
        <div className="mx-auto max-w-5xl px-4 md:px-6 py-20 md:py-28">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
            Meet the founder
          </p>
          <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
            The website you're on was designed by{" "}
            <span className="text-primary">RankMyLocal</span>
          </h1>
          <p className="mt-6 text-lg text-white/85 max-w-2xl leading-relaxed">
            RankMyLocal is a UK web design and local SEO practice run by its
            founder — the same person who designs the sites, writes the schema,
            and manages Google rankings for a small roster of independent
            businesses. If you'd like the same for your business, the fastest
            way in is a direct email.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={mailtoHref("New enquiry from woodseerautos.co.uk")}
              onClick={() => handleContactClick("hero_email")}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
            >
              <Mail className="h-4 w-4" />
              Email the founder
            </a>
            <a
              href={RML_URL}
              onClick={() => handleRmlClick("hero_visit")}
              target="_blank"
              rel="sponsored noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 px-6 py-3.5 text-sm font-bold text-white transition-colors"
            >
              Visit RankMyLocal <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <p className="mt-5 text-xs text-white/60">
            Direct line to the founder ·{" "}
            <a
              href={mailtoHref("New enquiry from woodseerautos.co.uk")}
              onClick={() => handleContactClick("hero_email_inline")}
              className="text-white/85 underline hover:text-white"
            >
              {FOUNDER_EMAIL}
            </a>
          </p>
        </div>
      </section>

      {/* FOUNDER CARD */}
      <AnimatedSection>
        <section className="py-16 md:py-24 bg-background">
          <div className="mx-auto max-w-5xl px-4 md:px-6">
            <div className="relative rounded-2xl border border-border bg-card p-8 md:p-10 shadow-sm">
              <span
                className="absolute left-0 top-6 bottom-6 w-[3px] rounded-r bg-primary"
                aria-hidden="true"
              />
              <p className="eyebrow-muted">Founder profile</p>
              <h2 className="mt-2 text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">
                Designed and managed by one person who actually cares
              </h2>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                RankMyLocal is deliberately small. Every project is designed,
                built and managed by the founder — not handed off to a junior
                or an offshore team. That's why the site you're on feels
                considered: the typography, the animations, the phone-first
                CTAs and the local SEO plumbing were all decided by one person
                who owns the outcome.
              </p>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                If you're an independent UK business — a garage, a trade, a
                restaurant, a professional practice — and you're tired of
                template-shop websites that don't rank, this is the person to
                talk to.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href={mailtoHref("Web design enquiry")}
                  onClick={() => handleContactClick("founder_card_email")}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Email {FOUNDER_EMAIL}
                </a>
                <span className="text-xs text-muted-foreground">
                  Replies personally, usually within one working day.
                </span>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* CAPABILITIES */}
      <AnimatedSection>
        <section className="py-16 md:py-24 bg-secondary/30">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <div className="max-w-2xl">
              <p className="eyebrow-muted">What's on offer</p>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
                Websites that are designed to rank and convert
              </h2>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                The same capabilities used to build and run woodseerautos.co.uk
                — available to a small number of new clients each quarter.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {CAPABILITIES.map((c) => (
                <div
                  key={c.title}
                  className="relative rounded-xl border border-border bg-card p-6 pl-7 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span
                    className="absolute left-0 top-4 bottom-4 w-[3px] rounded-r bg-primary"
                    aria-hidden="true"
                  />
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary border border-primary/20 shrink-0">
                      <c.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="text-base font-bold text-foreground">
                        {c.title}
                      </h3>
                      <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                        {c.body}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* PROCESS */}
      <AnimatedSection>
        <section className="py-16 md:py-24 bg-background">
          <div className="mx-auto max-w-5xl px-4 md:px-6">
            <div className="max-w-2xl">
              <p className="eyebrow-muted">How it works</p>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
                From first email to first ranking
              </h2>
            </div>
            <ol className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              {PROCESS.map((step) => (
                <li
                  key={step.title}
                  className="relative rounded-xl border border-border bg-card p-6 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary border border-primary/20 shrink-0">
                      <step.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="text-base font-bold text-foreground">
                        {step.title}
                      </h3>
                      <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                        {step.body}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      </AnimatedSection>

      {/* PROOF STRIP */}
      <section className="surface-dark grain-overlay py-14 md:py-16">
        <div className="mx-auto max-w-5xl px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
                Proof of work
              </p>
              <h2 className="mt-2 text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                You're looking at the case study
              </h2>
              <p className="mt-3 text-white/80 leading-relaxed">
                Every page, animation, area landing page, schema block and
                Google Business Profile touchpoint on woodseerautos.co.uk is
                RankMyLocal's work. Read the full breakdown on the{" "}
                <Link
                  to="/managed-by"
                  className="text-white underline hover:text-primary"
                >
                  Managed By
                </Link>{" "}
                page.
              </p>
            </div>
            <div className="flex md:justify-end">
              <Link
                to="/managed-by"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 px-5 py-3 text-sm font-bold text-white transition-colors"
              >
                See the case study <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <AnimatedSection>
        <section className="py-16 md:py-24 bg-background">
          <div className="mx-auto max-w-3xl px-4 md:px-6">
            <div className="max-w-2xl">
              <p className="eyebrow-muted">Common questions</p>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
                Frequently asked
              </h2>
            </div>
            <div className="mt-8">
              <FAQAccordion faqs={faqs} title="Frequently asked" />
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* FINAL CTA */}
      <section className="py-16 md:py-20 bg-secondary/40 border-t border-border">
        <div className="mx-auto max-w-3xl px-4 md:px-6 text-center">
          <CheckCircle2 className="h-8 w-8 text-primary mx-auto" />
          <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
            Ready to talk about your website?
          </h2>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed">
            One short email is all it takes. Tell the founder about your
            business and you'll get a personal reply — not an automated funnel.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href={mailtoHref("New enquiry from woodseerautos.co.uk")}
              onClick={() => handleContactClick("footer_email")}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
            >
              <Mail className="h-4 w-4" />
              Email {FOUNDER_EMAIL}
            </a>
            <a
              href={RML_URL}
              onClick={() => handleRmlClick("footer_visit")}
              target="_blank"
              rel="sponsored noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card hover:bg-secondary px-6 py-3.5 text-sm font-bold text-foreground transition-colors"
            >
              Visit RankMyLocal <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
